import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  TextField,
  Typography
} from "@material-ui/core";
import { Button } from "components";
import { useFormik } from "formik";
import { usePopup } from "hooks/usePopup";
import purchaseSupplyConfigService from "services/purchaseSupplyService";
import { BUYER_GROUP_ID } from "settings";
import { Drawer } from "shared";
import AutoCompleteProductBaseFormik from "shared/AutoCompleteProductBaseFormik";
import AutoCompleteUserFormik from "shared/AutoCompleteUsers";
import { decimalMask, convertToFloat } from "utils/inputMasks";
import * as Yup from "yup";

import { useStyles } from "./styles";

export const WEEK_DAYS = [
  { value: 0, label: "Segunda", short: "Seg" },
  { value: 1, label: "Terça", short: "Ter" },
  { value: 2, label: "Quarta", short: "Qua" },
  { value: 3, label: "Quinta", short: "Qui" },
  { value: 4, label: "Sexta", short: "Sex" },
  { value: 5, label: "Sábado", short: "Sáb" },
  { value: 6, label: "Domingo", short: "Dom" }
];

const arrayToDaysObject = arrivalDays => {
  const daysObj = {};
  WEEK_DAYS.forEach(day => {
    daysObj[`day_${day.value}`] = (arrivalDays || []).includes(day.value);
  });
  return daysObj;
};

const daysObjectToArray = daysObj => {
  return WEEK_DAYS.filter(day => daysObj[`day_${day.value}`]).map(
    day => day.value
  );
};

const validationSchema = Yup.object({
  productBase: Yup.object().nullable().required("Produto é obrigatório"),
  responsible: Yup.object().nullable().required("Responsável é obrigatório"),
  minOrderQuantity: Yup.string().required("Pedido mínimo é obrigatório")
}).test(
  "at-least-one-day",
  "Selecione pelo menos um dia de chegada",
  values => {
    const arrivalDays = daysObjectToArray(values);
    return arrivalDays.length > 0;
  }
);

export const FormDrawer = ({ open, onClose, initialData, onSuccess }) => {
  const classes = useStyles();
  const { addPopup } = usePopup();
  const isEditMode = !!initialData?.id;

  const prepareInitialValues = data => {
    if (!data) {
      return {
        id: null,
        productBase: null,
        responsible: null,
        minOrderQuantity: "",
        ...arrayToDaysObject([])
      };
    }

    // Converte o valor numérico para string com vírgula para exibição
    const minOrderValue = data.min_order_quantity
      ? data.min_order_quantity.toString().replace(".", ",")
      : "";

    return {
      id: data.id,
      productBase: data.productBase,
      responsible: data.responsible,
      minOrderQuantity: minOrderValue,
      ...arrayToDaysObject(data.arrival_days || [])
    };
  };

  const formik = useFormik({
    initialValues: prepareInitialValues(initialData),
    validationSchema,
    enableReinitialize: true,
    onSubmit: async values => {
      try {
        const arrivalDays = daysObjectToArray(values);

        const payload = {
          product_base_name: values.productBase?.name,
          responsible_id: values.responsible?.id || null,
          responsible_name: values.responsible?.username || null,
          arrival_days: arrivalDays,
          min_order_quantity: convertToFloat(values.minOrderQuantity)
        };

        if (isEditMode) {
          await purchaseSupplyConfigService.update(values.id, payload);
          addPopup({
            type: "success",
            title: "Configuração atualizada com sucesso!"
          });
        } else {
          await purchaseSupplyConfigService.create({
            ...payload,
            product_base_id: values.productBase.id
          });
          addPopup({
            type: "success",
            title: "Configuração criada com sucesso!"
          });
        }

        formik.resetForm();
        onSuccess?.();
        onClose();
      } catch (error) {
        addPopup({
          type: "error",
          title: "Desculpe, ocorreu um erro",
          description:
            error?.response?.data?.message ||
            "Não foi possível salvar a configuração."
        });
      }
    }
  });

  const drawerTitle = isEditMode ? "Editar configuração" : "Nova configuração";

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={drawerTitle}
      anchor="right"
      maxWidth="560px"
    >
      <form onSubmit={formik.handleSubmit}>
        <Box className={classes.drawerContent}>
          <AutoCompleteProductBaseFormik
            formik={formik}
            name="productBase"
            label="Produto *"
            disabled={isEditMode}
          />

          <AutoCompleteUserFormik
            formik={formik}
            name="responsible"
            label="Comprador responsável *"
            groupId={BUYER_GROUP_ID}
          />

          <TextField
            fullWidth
            required
            name="minOrderQuantity"
            type="text"
            variant="outlined"
            label="Pedido Mínimo"
            value={formik.values.minOrderQuantity}
            onChange={e => {
              const maskedValue = decimalMask(e.target.value);
              formik.setFieldValue("minOrderQuantity", maskedValue);
            }}
            disabled={formik.isSubmitting}
            error={
              formik.touched.minOrderQuantity &&
              Boolean(formik.errors.minOrderQuantity)
            }
            helperText={
              formik.touched.minOrderQuantity && formik.errors.minOrderQuantity
            }
          />

          <Box>
            <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
              Dias de chegada *
            </Typography>
            <Grid container spacing={1} className={classes.daysGroup}>
              {WEEK_DAYS.map(day => (
                <Grid item xs={6} sm={4} key={day.value}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(formik.values[`day_${day.value}`])}
                          onChange={formik.handleChange}
                          name={`day_${day.value}`}
                          color="primary"
                          disabled={formik.isSubmitting}
                        />
                      }
                      label={day.label}
                    />
                  </FormGroup>
                </Grid>
              ))}
            </Grid>
            {formik.errors["at-least-one-day"] && formik.submitCount > 0 && (
              <Typography
                variant="caption"
                color="error"
                style={{ marginTop: 8, display: "block" }}
              >
                Selecione pelo menos um dia de chegada
              </Typography>
            )}
          </Box>

          <Box className={classes.footerButtons}>
            <Button
              fullWidth
              variant="outlined"
              onClick={onClose}
              disabled={formik.isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={
                formik.isSubmitting ||
                !formik.dirty ||
                !formik.isValid ||
                daysObjectToArray(formik.values).length === 0
              }
            >
              {isEditMode ? "Salvar alterações" : "Criar configuração"}
            </Button>
          </Box>
        </Box>
      </form>
    </Drawer>
  );
};
